import type { BudgetEntityRef, BudgetEntityType } from '../types/budget'
import type { TimelineElement } from '../types/timelineElement'

export interface BudgetChainNode {
  id: string
  type?: BudgetEntityType | string
  kind?: string
  title?: string
  label?: string
  parentId?: string | null
  parentContainerId?: string | null
  docPath?: string
}

export interface BudgetEntityChainItem extends BudgetEntityRef {
  title?: string
}

const normalizeEntityType = (rawType?: string | null): BudgetEntityType => {
  const upper = (rawType ?? '').toUpperCase()
  if (upper === 'CHECKLIST') return 'CHECKLIST'
  if (upper === 'GOAL') return 'GOAL'
  return 'TASK'
}

export const budgetEntityDocPath = (type: BudgetEntityType, id: string): string => {
  return type === 'CHECKLIST' ? `checklists/${id}` : `elements/${id}`
}

const getDocPath = budgetEntityDocPath

/**
 * Build an entity chain leaf-first: [leaf, parent, ..., goal].
 * - Follows parentId for elements, or parentContainerId / parentId for checklists.
 * - Stops at missing parent, when root (GOAL) is reached, or when a cycle is detected.
 */
export const buildBudgetEntityChain = (
  leaf: BudgetChainNode,
  elements: TimelineElement[] = [],
): BudgetEntityChainItem[] => {
  if (!leaf || !leaf.id) return []

  const elementMap = new Map<string, TimelineElement>()
  elements.forEach((el) => {
    if (el && el.id) {
      elementMap.set(el.id, el)
    }
  })

  const visited = new Set<string>()
  const chain: BudgetEntityChainItem[] = []

  const matchingElement = elementMap.get(leaf.id)
  const leafType = normalizeEntityType(leaf.type || leaf.kind || matchingElement?.type)
  const leafTitle = leaf.title ?? leaf.label ?? matchingElement?.title ?? ''
  const leafDocPath = leaf.docPath || getDocPath(leafType, leaf.id)

  chain.push({
    type: leafType,
    id: leaf.id,
    title: leafTitle,
    docPath: leafDocPath,
  })
  visited.add(leaf.id)

  if (leafType === 'GOAL') {
    return chain
  }

  let currentId: string | null = leafType === 'CHECKLIST'
    ? (leaf.parentContainerId ?? leaf.parentId ?? matchingElement?.parentId ?? null)
    : (leaf.parentId ?? matchingElement?.parentId ?? null)
  while (currentId) {
    if (visited.has(currentId)) {
      break
    }
    visited.add(currentId)

    const parent = elementMap.get(currentId)
    if (!parent) {
      break
    }

    const parentType = normalizeEntityType(parent.type)
    chain.push({
      type: parentType,
      id: parent.id,
      title: parent.title || '',
      docPath: getDocPath(parentType, parent.id),
    })

    if (parentType === 'GOAL') {
      break
    }

    currentId = parent.parentId ?? null
  }

  return chain
}
